"use client";

import Link from "next/link";
import { useEffect } from "react";
import { AlertTriangle, ArrowRight, RotateCcw } from "react-feather";

import CTAButton from "@/components/atoms/buttons/CTAButton";

// --------------------------------------------------------------------------------------
// Root error boundary — catches runtime errors thrown in any page/component.
// Mirrors the 404 page's visual language (light bg, gradient headline, popular
// destinations) but tailored for server/runtime failures: includes a "Try
// again" reset action and contextually different copy.
// --------------------------------------------------------------------------------------

const POPULAR_LINKS: { label: string; href: string; description: string }[] = [
  {
    label: "Penetration Testing",
    href: "/services/vulnerability-management",
    description: "Web, mobile, network, API, cloud, IoT",
  },
  {
    label: "Compliance",
    href: "/services/compliance-management",
    description: "ISO 27001, SOC 2, PCI DSS, HIPAA, DPDP",
  },
  {
    label: "Data Privacy",
    href: "/services/data-privacy-program",
    description: "DPDP & GDPR consulting, DPIA, RoPA",
  },
];

export default function Error({
  error,
  reset,
}: {
  error?: Error & { digest?: string };
  reset?: () => void;
}) {
  useEffect(() => {
    // Surface the error to whatever monitoring the app loads (Clarity,
    // Sentry, etc.). Safe no-op if console is overridden in prod.
    if (error) console.error("[ErrorBoundary]", error);
  }, [error]);

  return (
    <main className="relative isolate min-h-[calc(100vh-60px)] overflow-hidden bg-white">
      {/* Same soft radial ornament as the 404 — keeps the two error
          surfaces visually consistent. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10"
      >
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(204,1,0,0.06),transparent_60%)]" />
        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-black-100 to-transparent" />
      </div>

      <section className="container mx-auto flex min-h-[calc(100vh-60px)] flex-col items-center justify-center px-6 py-16 text-center md:py-20">
        {/* 500 — gradient fill, primary→red for server-error sentiment */}
        <p
          aria-hidden="true"
          className="bg-gradient-to-b from-primary-900 to-[#CC0100] bg-clip-text font-bricolage-grotesque text-[clamp(7rem,18vw,14rem)] font-semibold leading-none text-transparent"
        >
          500
        </p>

        <h1 className="mt-4 font-bricolage-grotesque text-[clamp(1.875rem,2.5vw+1rem,3rem)] font-medium !leading-[1.1] text-black-900">
          Something on our end just crashed.
        </h1>

        <p className="mx-auto mt-5 max-w-xl text-[clamp(1rem,0.4vw+0.875rem,1.125rem)] leading-relaxed text-black-700">
          Don&apos;t worry — your data is safe and our SOC is already on
          it. This was either a transient hiccup or a bug we&apos;ll be
          fixing shortly. Try again, or head somewhere else.
        </p>

        {/* Optional digest for support — only when present (Next.js
            attaches this in production for grep'ing server logs). */}
        {error?.digest && (
          <p className="mt-3 text-xs font-normal uppercase tracking-wider text-black-500">
            <AlertTriangle size={12} className="mr-1.5 inline-block" />
            Reference: <span className="font-mono">{error.digest}</span>
          </p>
        )}

        {/* Site-wide CTA rule applied:
              orange (primary "Try Again" = retry conversion intent) →
              primary blue (secondary "Back to Home" = navigation). */}
        <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
          {reset && (
            <CTAButton
              id="error-try-again-cta"
              onClick={reset}
              variant="orange"
              size="md"
              showIcon={false}
              className="w-fit"
            >
              <span className="inline-flex items-center gap-2">
                <RotateCcw size={18} />
                Try Again
              </span>
            </CTAButton>
          )}

          <CTAButton
            id="error-home-cta"
            href="/"
            variant="primary"
            size="md"
            showIcon
            iconType="arrow"
            iconPosition="right"
            className="w-fit"
          >
            Back to Home
          </CTAButton>
        </div>

        {/* Lighter wayfinding grid — fewer items than 404 since the
            user's primary action here is "try again" or "report". */}
        <div className="mt-14 w-full max-w-3xl text-left">
          <p className="mb-5 text-center font-bricolage-grotesque text-xs font-semibold uppercase tracking-[0.25em] text-primary-900">
            Or browse popular destinations
          </p>

          <ul className="grid gap-3 sm:grid-cols-3">
            {POPULAR_LINKS.map((link) => (
              <li key={link.href}>
                <Link
                  href={link.href}
                  className="group flex h-full flex-col rounded-xl border border-black-100 bg-white p-5 transition-all hover:-translate-y-0.5 hover:border-primary-900 hover:shadow-lg"
                >
                  <span className="flex items-center justify-between font-bricolage-grotesque text-base font-medium text-black-900 group-hover:text-primary-900">
                    {link.label}
                    <ArrowRight
                      size={16}
                      className="text-black-400 transition-all duration-300 group-hover:translate-x-1 group-hover:text-primary-900"
                    />
                  </span>
                  <span className="mt-1 text-sm text-black-600">
                    {link.description}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </main>
  );
}
